import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import mtstLogo from "../assets/img/mtst-logo.png";

const NAV_ITEMS = [
  { to: "/", label: "홈" },
  { to: "/technology", label: "기술 및 서비스" },
  { to: "/cases", label: "사례" },
  {
    label: "회사",
    children: [
      { to: "/introduction", label: "회사 소개" },
      { to: "/mission-vision", label: "미션 & 비전" },
      { to: "/careers", label: "채용" },
    ],
  },
];

const Header = () => {
  const { pathname } = useLocation();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setScrolled] = useState(false);

  // 모바일 메뉴가 열려있을 때 스크롤 방지
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // 스크롤 시 헤더를 얇게 줄이고 블러 처리
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 메뉴 클릭 핸들러
  const handleClick = (path) => {
    if (pathname === path) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const isItemActive = (item) =>
    item.to
      ? pathname === item.to
      : item.children?.some((c) => c.to === pathname);

  return (
    <>
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? "bg-white/80 shadow-md backdrop-blur-md"
          : "bg-white shadow-sm"
      }`}
    >
      <div
        className={`mx-auto grid max-w-7xl grid-cols-2 items-center px-4 transition-all duration-300 sm:px-6 lg:grid-cols-3 lg:px-8 ${
          isScrolled ? "h-16" : "h-20"
        }`}
      >
        {/* 로고 */}
        <Link to="/" onClick={() => handleClick("/")} className="shrink-0 justify-self-start">
          <img
            src={mtstLogo}
            alt="MTST Logo"
            className={`w-auto transition-all duration-300 ${
              isScrolled ? "h-8 md:h-9" : "h-9 md:h-11"
            }`}
          />
        </Link>

        {/* 데스크탑 메뉴 (가운데 정렬) */}
        <ul className="hidden lg:col-start-2 lg:flex lg:items-center lg:justify-center lg:gap-8">
          {NAV_ITEMS.map((item) => (
            <li key={item.label} className={item.children ? "group relative" : undefined}>
              {item.to ? (
                <Link
                  to={item.to}
                  onClick={() => handleClick(item.to)}
                  className={`text-base font-medium transition-colors hover:text-primary-600 ${
                    isItemActive(item)
                      ? "font-semibold text-primary-700"
                      : "text-gray-700"
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <>
                  <button
                    type="button"
                    className={`flex items-center gap-1 text-base font-medium transition-colors hover:text-primary-600 ${
                      isItemActive(item)
                        ? "font-semibold text-primary-700"
                        : "text-gray-700"
                    }`}
                  >
                    {item.label}
                    <ChevronDown
                      className="h-4 w-4 transition-transform group-hover:rotate-180"
                      strokeWidth={2}
                    />
                  </button>
                  <div className="invisible absolute left-1/2 top-full z-10 w-40 -translate-x-1/2 pt-3 opacity-0 transition-all group-hover:visible group-hover:opacity-100">
                    <div className="rounded-lg border border-gray-100 bg-white p-2 shadow-lg">
                      {item.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          onClick={() => handleClick(child.to)}
                          className={`block rounded-md px-3 py-2 text-sm transition-colors ${
                            pathname === child.to
                              ? "bg-primary-50 font-semibold text-primary-700"
                              : "text-gray-700 hover:bg-primary-50 hover:text-primary-700"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>

        {/* CTA + 모바일 햄버거 */}
        <div className="col-start-2 flex items-center justify-end gap-4 lg:col-start-3">
          <a
            href="mailto:mtst@mtstech.kr"
            className="hidden items-center rounded-md bg-primary-800 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700 lg:inline-flex"
          >
            도입 문의
          </a>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md p-2 text-gray-700 lg:hidden"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="메뉴 열기"
          >
            <Menu size={26} />
          </button>
        </div>
      </div>
    </header>

      {/* 모바일 메뉴 오버레이 */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 lg:hidden ${
          isMobileMenuOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* 모바일 메뉴 (사이드바) */}
      <nav
        className={`fixed inset-y-0 right-0 z-50 flex w-4/5 max-w-sm flex-col overflow-y-auto bg-white px-6 py-6 shadow-xl transition-transform duration-300 lg:hidden ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <button
          type="button"
          className="ml-auto rounded-md p-2 text-gray-700"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="메뉴 닫기"
        >
          <X size={26} />
        </button>
        <ul className="mt-6 flex flex-col gap-2 text-center">
          {NAV_ITEMS.map((item) =>
            item.to ? (
              <li key={item.label}>
                <Link
                  to={item.to}
                  onClick={() => handleClick(item.to)}
                  className={`block rounded-md px-4 py-3 text-lg font-medium transition-colors ${
                    isItemActive(item)
                      ? "bg-primary-50 font-semibold text-primary-700"
                      : "text-gray-700"
                  }`}
                >
                  {item.label}
                </Link>
              </li>
            ) : (
              <li key={item.label}>
                <div className="px-4 pt-3 text-sm font-semibold text-gray-400">
                  {item.label}
                </div>
                {item.children.map((child) => (
                  <Link
                    key={child.to}
                    to={child.to}
                    onClick={() => handleClick(child.to)}
                    className={`block rounded-md px-4 py-3 text-lg font-medium transition-colors ${
                      pathname === child.to
                        ? "bg-primary-50 font-semibold text-primary-700"
                        : "text-gray-700"
                    }`}
                  >
                    {child.label}
                  </Link>
                ))}
              </li>
            )
          )}
        </ul>
        <a
          href="mailto:mtst@mtstech.kr"
          onClick={() => setMobileMenuOpen(false)}
          className="mt-6 inline-flex items-center justify-center rounded-md bg-primary-800 px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-700"
        >
          도입 문의
        </a>
      </nav>
    </>
  );
};

export default Header;
